import React, { useState, useEffect, useCallback, useRef } from "react";
import { TrendingUp, TrendingDown, Loader2, Trash2, Youtube, Sparkles, Minus, ArrowUpRight, ArrowDownRight, Clapperboard, AlertTriangle, ShieldCheck, Mail, MailCheck, Check } from "lucide-react";
import { videoAnalyze, videoAnalyzeBatch, videoList, videoDelete, videoSettingsGet, videoSettingsSave, videoEmailNow } from "../lib/api";
import { toast } from "sonner";
import { OpenBBResearch } from "./OpenBBResearch";
import { BullHuntCard } from "./BullHuntCard";
import { OptionsFlowCard } from "./OptionsFlowCard";

const sentimentStyle = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "bullish") return { c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", Icon: ArrowUpRight };
  if (v === "bearish") return { c: "text-rose-300 border-rose-500/40 bg-rose-500/10", Icon: ArrowDownRight };
  if (v === "mixed") return { c: "text-amber-300 border-amber-500/40 bg-amber-500/10", Icon: Minus };
  return { c: "text-cyan-200 border-cyan-500/40 bg-cyan-500/10", Icon: Minus };
};

const PickRow = ({ item, positive }) => (
  <div className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`video-${positive ? "pick" : "reject"}-row`}>
    <div className={`mt-0.5 shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${positive ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
      {positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
    </div>
    <div className="min-w-0">
      <p className="text-cyan-50 text-sm font-bold">
        {item.ticker ? <span className={`font-mono ${positive ? "text-emerald-300" : "text-rose-300"}`}>{item.ticker}</span> : null}
        {item.ticker && item.name ? " · " : ""}
        <span className="text-cyan-100">{item.name}</span>
      </p>
      {item.reason && <p className="text-cyan-200/70 text-xs mt-0.5 leading-relaxed">{item.reason}</p>}
      {item.jev && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]" data-testid="pick-jev-verdict">
          <span className="text-[#00F0FF] font-bold uppercase tracking-wider flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" /> Jev</span>
          {item.jev.conviction_level && <span className="px-1.5 py-0.5 rounded bg-[#00F0FF11] border border-[#00F0FF22] text-cyan-100/80">{item.jev.conviction_level}</span>}
          {item.jev.actionable != null && (
            <span className={item.jev.actionable >= 0.5 ? "text-emerald-300/80" : "text-cyan-200/40"}>
              {item.jev.actionable >= 0.5 ? "act now" : "can wait"} {Math.round(item.jev.actionable * 100)}%
            </span>
          )}
        </div>
      )}
    </div>
  </div>
);

const Brief = ({ r, onDelete, onEmail }) => {
  const [emailing, setEmailing] = useState(false);
  const doEmail = async () => {
    setEmailing(true);
    try { await onEmail(r.id); } finally { setEmailing(false); }
  };
  if (r.status === "processing") {
    return (
      <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 flex items-center gap-3" data-testid="video-processing-card">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-300 shrink-0" />
        <div className="min-w-0">
          <p className="text-cyan-100 text-sm font-semibold">Gemini is watching this video…</p>
          <p className="text-cyan-300/40 text-xs truncate">{r.src_label && r.src_label !== "manual" ? `${r.src_label} · ` : ""}{r.url}</p>
        </div>
      </div>
    );
  }
  if (r.status === "error") {
    return (
      <div className="bg-rose-500/5 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="video-error-card">
        <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-rose-200 text-sm font-semibold">Couldn't analyze this one</p>
          <p className="text-rose-300/70 text-xs mt-0.5">{r.error || "Try again in a moment."}</p>
          <p className="text-cyan-300/30 text-xs truncate mt-1">{r.url}</p>
        </div>
        {onDelete && <button onClick={() => onDelete(r.id)} data-testid="video-delete-btn" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
      </div>
    );
  }
  const { c, Icon } = sentimentStyle(r.sentiment);
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-4" data-testid="video-brief-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-cyan-50 font-bold text-base leading-snug" data-testid="video-brief-title">{r.title}</h3>
          <p className="text-cyan-300/50 text-xs mt-0.5">
            {r.src_label && r.src_label !== "manual" ? <span className="text-[#00F0FF] font-semibold">{r.src_label} · </span> : null}
            {r.channel ? `${r.channel} · ` : ""}{r.source === "youtube" ? "YouTube" : "Video"}
            {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {r.sentiment && <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${c}`}><Icon className="w-3.5 h-3.5" />{r.sentiment}</span>}
          <button onClick={doEmail} disabled={emailing} data-testid="video-email-btn" title={r.emailed ? "Emailed — click to resend" : "Email me this brief"}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${r.emailed ? "text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/10" : "text-cyan-200/70 border-[#00F0FF33] hover:bg-[#00F0FF11]"}`}>
            {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : r.emailed ? <MailCheck className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{r.emailed ? "Emailed" : "Email"}</span>
          </button>
          {onDelete && <button onClick={() => onDelete(r.id)} data-testid="video-delete-btn" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {r.summary && <p className="text-cyan-100/90 text-sm leading-relaxed" data-testid="video-brief-summary">{r.summary}</p>}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3">
          <p className="text-emerald-300 text-xs font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Stocks Picked</p>
          {r.picks?.length ? r.picks.map((p, i) => <PickRow key={i} item={p} positive />) : <p className="text-cyan-200/40 text-xs py-2">None mentioned.</p>}
        </div>
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-3">
          <p className="text-rose-300 text-xs font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5" /> Stocks Rejected</p>
          {r.rejects?.length ? r.rejects.map((p, i) => <PickRow key={i} item={p} positive={false} />) : <p className="text-cyan-200/40 text-xs py-2">None mentioned.</p>}
        </div>
      </div>

      {r.tickers?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {r.tickers.map((t, i) => <span key={i} className="px-2 py-0.5 rounded-md bg-[#00F0FF11] border border-[#00F0FF22] text-cyan-200 text-xs font-mono">{t}</span>)}
        </div>
      )}

      {r.notable?.length > 0 && (
        <div className="space-y-1">
          <p className="text-cyan-300/60 text-xs font-bold uppercase tracking-wide">Key takeaways</p>
          <ul className="list-disc list-inside space-y-0.5">
            {r.notable.map((n, i) => <li key={i} className="text-cyan-100/80 text-xs leading-relaxed">{n}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function NexusVideoIntel({ session }) {
  const userId = session.user_id;
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [ytAuto, setYtAuto] = useState(false);
  const [mailCfg, setMailCfg] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await videoList(userId);
      setItems(r.items || []);
      setYtAuto(!!r.yt_auto_enabled);
      return r.items || [];
    } catch (e) { return []; }
  }, [userId]);

  const loadCfg = useCallback(async () => {
    try { const c = await videoSettingsGet(userId); setMailCfg(c); setEmailDraft(c.email || ""); } catch (e) {}
  }, [userId]);

  useEffect(() => { load(); loadCfg(); }, [load, loadCfg]);

  // poll while anything is still processing
  useEffect(() => {
    const anyProcessing = items.some((x) => x.status === "processing");
    if (anyProcessing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const list = await load();
        if (!list.some((x) => x.status === "processing")) {
          clearInterval(pollRef.current); pollRef.current = null;
        }
      }, 5000);
    }
    return () => { if (pollRef.current && !items.some((x) => x.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, load]);

  const analyze = async () => {
    const list = urls.split(/[\n,\s]+/).map((u) => u.trim()).filter((u) => u.startsWith("http"));
    if (!list.length) { toast.error("Paste one or more video links"); return; }
    setBusy(true);
    try {
      if (list.length === 1) await videoAnalyze(userId, list[0]);
      else await videoAnalyzeBatch(userId, list);
      setUrls("");
      toast.success(list.length === 1 ? "Analyzing — the brief will appear shortly" : `Queued ${list.length} videos`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't queue that");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await videoDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {}
  };

  const emailBrief = async (id) => {
    try {
      const r = await videoEmailNow(userId, id);
      toast.success(`Emailed to ${r.emailed_to}`);
      setItems((h) => h.map((x) => (x.id === id ? { ...x, emailed: true } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send email"); }
  };

  const saveCfg = async (patch) => {
    setSavingCfg(true);
    try {
      const c = await videoSettingsSave(userId, patch);
      setMailCfg(c); setEmailDraft(c.email || "");
      toast.success("Email settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSavingCfg(false); }
  };

  const processingCount = items.filter((x) => x.status === "processing").length;

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="video-intel-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <OptionsFlowCard />
        <BullHuntCard />
        <OpenBBResearch userId={userId} />
        <div>
          <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Clapperboard className="w-6 h-6" /> Video Intel</h2>
          <p className="text-cyan-200/60 text-sm mt-1">Drop one or many finance video links (YouTube). Nexus watches them in the background, emails you each brief the moment it's ready, and keeps them all here to read at your convenience.</p>
        </div>

        {ytAuto && (
          <div className="bg-emerald-500/8 border border-emerald-500/30 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="madmoney-auto-on">
            <Sparkles className="w-4 h-4 text-emerald-300" />
            <p className="text-emerald-100 text-xs">Auto Mad Money digest is <b>ON</b> — new Cramer videos are summarized automatically each weekday evening.</p>
          </div>
        )}

        {mailCfg && (
          <div className="bg-[#060b18] border border-[#00F0FF22] rounded-xl p-3 space-y-2" data-testid="video-email-settings">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-cyan-300/60" />
              <span className="text-cyan-100 text-sm font-semibold">Auto-email briefs</span>
              <button
                onClick={() => saveCfg({ auto_email: !mailCfg.auto_email })}
                disabled={savingCfg}
                data-testid="video-autoemail-toggle"
                className={`ml-auto relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${mailCfg.auto_email ? "bg-[#00F0FF]" : "bg-white/10"}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-[#030712] transition-transform ${mailCfg.auto_email ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
            </div>
            <p className="text-cyan-300/50 text-xs">
              {mailCfg.auto_email
                ? "Each brief is emailed the moment it's ready."
                : "Auto-email is off — use the Email button on any brief to send it."}
            </p>
            <div className="flex items-center gap-2">
              <input value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)} type="email" placeholder="recipient email" data-testid="video-email-input"
                className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm outline-none" />
              <button onClick={() => saveCfg({ email: emailDraft })} disabled={savingCfg || !emailDraft.trim()} data-testid="video-email-save"
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-50 flex items-center gap-1">
                {savingCfg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
            </div>
            {!mailCfg.resend_configured && <p className="text-amber-300/70 text-xs">⚠ Nexus email (Resend) isn't connected — briefs can't be emailed yet.</p>}
          </div>
        )}

        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-3 space-y-2">
          <div className="flex items-start gap-2 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2">
            <Youtube className="w-4 h-4 text-cyan-300/60 shrink-0 mt-2" />
            <textarea value={urls} onChange={(e) => setUrls(e.target.value)}
              placeholder={"Paste one link, or many (one per line):\nhttps://youtu.be/…\nhttps://youtu.be/…"} data-testid="video-url-input" rows={2}
              className="w-full bg-transparent py-1.5 text-cyan-50 text-sm outline-none resize-none" />
          </div>
          <button onClick={analyze} disabled={busy} data-testid="video-analyze-btn"
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Queuing…</> : <><Sparkles className="w-4 h-4" /> Summarize</>}
          </button>
        </div>

        {processingCount > 0 && <p className="text-cyan-200/50 text-xs text-center">{processingCount} video{processingCount > 1 ? "s" : ""} analyzing in the background — briefs appear as they finish.</p>}

        {items.length > 0 ? (
          <div className="space-y-3">
            {items.map((h) => <Brief key={h.id} r={h} onDelete={del} onEmail={emailBrief} />)}
          </div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="video-empty">
            <Clapperboard className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No videos yet. Paste a CNBC / finance link above to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
